import React, { useState } from "react";
import LoggedOutPage from "../components/LoggedOutPage";
import { TextField, Button, Box, Alert, Stack } from "@mui/material";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { useFetch } from "./FetchProvider";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import * as z from "zod";
import { generateDefaults, zodResolver } from "../utils/form";
import { useErrorMessages } from "../utils/error-messages";

function useValidationSchema() {
    const {formatError} = useErrorMessages();

    return z
        .object({
            email: z
                .string()
                .email(formatError({ id: "email_error" }))
                .min(1, formatError({ id: "email_error" })),
        })
        .required();
}

type FormInputs = z.infer<ReturnType<typeof useValidationSchema>>;

const RequestPasswordReset = () => {
    const [serverError, setServerError] = useState<MessageDescriptor | null>(null);
    const [sentEmail, setSentEmail] = useState<string | null>(null);
    const schema = useValidationSchema();
    const {formatError, getError} = useErrorMessages();
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<FormInputs>({
        resolver: zodResolver(schema),
        defaultValues: generateDefaults(schema),
        mode: "onChange",
    });
    const fetch = useFetch();

    if (sentEmail)
        return (
            <LoggedOutPage title={<FormattedMessage id="reset_password.request" defaultMessage="Request password reset" />}>
                <FormattedMessage id="reset_password.request_sent" defaultMessage="Email was sent to {address}. Follow the link to reset your password." values={{ address: sentEmail }} />
            </LoggedOutPage>
        );

    // Handle form submission
    const onSubmit: SubmitHandler<FormInputs> = async (data) => {
        setServerError(null);

        console.log("Submitted email:", data.email);
        const resp = await fetch("/public/send-password-reset", {
            method: "POST",
            body: JSON.stringify({
                address: data.email,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            setSentEmail(data.email);
        } else if (resp.status === 404) {
            setError("email", { type: "server", message: formatError("reset_password.unknown_email") });
        } else setServerError(getError("reset_password.other_error"));
    };

    // Disable submit button if there are any errors
    const hasErrors = Object.keys(errors).length > 0;

    return (
        <LoggedOutPage title={<FormattedMessage id="reset_password.request" defaultMessage="Request password reset" />}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate width="30ch">
                <Stack spacing={2}>
                    <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                            <TextField
                                label={<FormattedMessage id="reset_password.address" defaultMessage="Email address" />}
                                {...field}
                                variant="outlined"
                                type="email"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                required
                                fullWidth
                            />
                        )}
                    />
                    {serverError && (
                        <Alert severity="error">
                            <FormattedMessage {...serverError} />
                        </Alert>
                    )}
                    <Button type="submit" variant="contained" disabled={hasErrors}>
                        <FormattedMessage id="submit" defaultMessage="Submit" />
                    </Button>
                </Stack>
            </Box>
        </LoggedOutPage>
    );
};

export default RequestPasswordReset;
